import React, { useState, useEffect, useRef } from 'react';
import CreditOffersService from 'services/credit-offers/credit-offers-service';
import { AdaptedCreditOffersResponse, CreditOfferTypes } from 'types/CreditOfferTypes';
import { userHasNonCreditAccount, userIsBnplEligible } from 'utilities/veryPay/veryPayUtilities';
import { SiteName } from 'types/Site';
import SkeletonCard from './Card/SkeletonCard';
import VeryPayCard from './Card/VeryPayCard';
import VeryPayDialog from './Dialog/VeryPayDialog';
import CreditToAccountCard from './Card/CreditToAccountCard';
import CreditToAccountDialog from './Dialog/CreditToAccountDialog';

type Props = {
  productPrice: string;
  productPriceDecimal: number;
  itemNumber: string;
  site?: SiteName;
};

function CreditOffers(props: Props) {
  const { productPrice, productPriceDecimal, itemNumber, site = 'very' } = props;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [nonCreditAccountUser, setNonCreditAccountUser] = useState(false);
  const [dialogRendered, setDialogRendered] = useState(false);
  const [creditOffers, setCreditOffers] = useState<AdaptedCreditOffersResponse | void>();
  const [loading, setLoading] = useState(true);
  const isFirstRender = useRef(true);

  const getCreditOffers = async () => {
    const isBnplEligible = userIsBnplEligible();
    const creditOffersService = new CreditOffersService();
    const response = await creditOffersService.getCreditOffers({
      price: productPriceDecimal,
      catalogueId: itemNumber,
      isBnplEligible,
    });

    setCreditOffers(response);
    setLoading(false);
  };

  useEffect(() => {
    getCreditOffers();
    setNonCreditAccountUser(userHasNonCreditAccount);
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (dialogRendered) {
      setDialogOpen(true);
    }
  }, [dialogRendered]);

  const openDialog = () => {
    if (dialogRendered) {
      setDialogOpen(true);
    } else {
      setDialogRendered(true);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  if (loading) return <SkeletonCard site={site} />;

  if (!creditOffers?.primary) return null;

  const { apr, offers, primary } = creditOffers;

  const renderOffer = () => {
    if (primary.type === CreditOfferTypes.CTA) {
      return (
        <>
          <CreditToAccountCard
            apr={apr}
            voucherCode={primary.attributes.voucherCode}
            offerTitle={primary.attributes.offerTitle}
            endDate={primary.attributes.endDate}
            termLength={primary.attributes.termLength}
            onClickCallback={openDialog}
            nonCreditAccountUser={nonCreditAccountUser}
          />

          {dialogRendered && (
            <CreditToAccountDialog
              dialogTitle={primary.attributes.offerTitle}
              voucherCode={primary.attributes.voucherCode}
              startDate={primary.attributes.startDate}
              endDate={primary.attributes.endDate}
              offerTerms1={primary.attributes.offerTerms1}
              offerTerms2={primary.attributes.offerTerms2}
              offerTerms3={primary.attributes.offerTerms3}
              offerTerms4={primary.attributes.offerTerms4}
              onClose={closeDialog}
              open={dialogOpen}
            />
          )}
        </>
      );
    }

    return (
      <>
        <VeryPayCard
          apr={apr}
          creditOffer={primary}
          nonCreditAccountUser={nonCreditAccountUser}
          onClickCallback={openDialog}
        />

        {dialogRendered && (
          <VeryPayDialog
            apr={apr}
            bnpl={offers[CreditOfferTypes.BNPL]}
            stc={offers[CreditOfferTypes.STC]}
            take3={offers[CreditOfferTypes.TAKE3]}
            nonCreditAccountUser={nonCreditAccountUser}
            productPrice={productPrice}
            open={dialogOpen}
            onClose={closeDialog}
          />
        )}
      </>
    );
  };

  return <div data-testid="credit-offers">{renderOffer()}</div>;
}

export default CreditOffers;
